import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile, UpdateProfileData } from '../lib/database.types';

export function useProfile(userId?: string) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setProfile(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: UpdateProfileData) => {
    if (!userId) throw new Error('No user logged in');

    const payload: any = {};
    
    if (updates.display_name !== undefined) {
      payload.display_name = updates.display_name;
    }
    if (updates.bio !== undefined) {
      payload.bio = updates.bio;
    }
    if (updates.avatar_url !== undefined) {
      payload.avatar_url = updates.avatar_url;
    }
    if (updates.role !== undefined) {
      payload.role = updates.role;
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    
    setProfile(data);
    return data;
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    refetch: fetchProfile,
  };
}
